import React from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from './Table';
import { TableWrapper, NoHotelsText } from '../HotelsTable/HotelsTable.styled';
import LoadingTableBody from './LoadingTableBody';

// Column definition interface
export interface ColumnDef<T = any> {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  className?: string;
  width?: string | number; // e.g., '200px', '20%', 200
}

// DataTable Props
export interface DataTableProps<T = any> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  variant?: 'default' | 'bordered' | 'striped';
  size?: 'sm' | 'md' | 'lg';
  // Sorting props
  sortKey?: string;
  sortOrder?: 'asc' | 'desc' | null;
  onSort?: (key: string) => void;
  // Row props
  renderRow?: (item: T, index: number) => React.ReactNode;
  getRowKey?: (item: T, index: number) => string | number;
  onRowClick?: (item: T, index: number) => void;
  onHoverRow?: (item: T, index: number) => void;
  // Styling
  className?: string;
  tableClassName?: string;
  tableHeaderClassName?: string;
  tableRowClassName?: string;
  showWrapper?: boolean;
}

/**
 * Reusable DataTable Component
 *
 * @example
 * // Basic usage with columns
 * <DataTable
 *   columns={[
 *     { key: 'name', label: 'Name', sortable: true },
 *     { key: 'email', label: 'Email', align: 'center' }
 *   ]}
 *   data={users}
 *   loading={isLoading}
 * />
 *
 * @example
 * // Custom row rendering
 * <DataTable
 *   columns={columns}
 *   data={hotels}
 *   renderRow={(hotel, index) => <HotelTableRow key={hotel.id} hotel={hotel} />}
 * />
 */
export const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  loadingMessage = 'Loading data...',
  emptyMessage = 'No data available',
  variant = 'striped',
  size = 'md',
  sortKey,
  sortOrder,
  onSort,
  renderRow,
  getRowKey,
  onRowClick,
  onHoverRow,
  className,
  tableClassName,
  tableHeaderClassName,
  tableRowClassName,
  showWrapper = true,
}: DataTableProps<T>) => {
  // Default row key generator
  const defaultGetRowKey = (item: T, index: number): string | number => {
    if (item.id !== undefined) return item.id;
    if (item._id !== undefined) return item._id;
    return index;
  };

  const rowKeyGetter = getRowKey || defaultGetRowKey;

  // Handle sort
  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  // Render table content
  const renderTableContent = () => {
    // Check if first column is a checkbox column
    const hasCheckboxColumn = columns.length > 0 && columns[0].key === 'select';
    const effectiveCols = hasCheckboxColumn ? columns.length : columns.length;

    // Extract column widths from column definitions
    const columnWidths = columns.map(col => col.width);

    return (
      <Table
        variant={variant}
        size={size}
        cols={effectiveCols}
        className={tableClassName}
        hasCheckbox={hasCheckboxColumn}
        columnWidths={columnWidths}
      >
        {/* Table Header */}
        <TableHeader className={`!rounded-none ${tableHeaderClassName || ''}`}>
          <TableRow>
            {columns.map(column => (
              <TableHeaderCell
                key={column.key}
                align={column.align || 'left'}
                sortable={column.sortable}
                sortDirection={sortKey === column.key ? sortOrder : null}
                onSort={
                  column.sortable ? () => handleSort(column.key) : undefined
                }
                className={column.className}
              >
                {column.headerRender ? column.headerRender() : column.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>

        {/* Loading State */}
        {loading && (
          <LoadingTableBody
            isFilterLoading={loading}
            cols={columns.length}
            loadingMessage={loadingMessage}
          />
        )}

        {/* Table Body */}
        {!loading && (
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => {
                // Use custom row renderer if provided
                if (renderRow) {
                  return (
                    <React.Fragment key={rowKeyGetter(item, index)}>
                      {renderRow(item, index)}
                    </React.Fragment>
                  );
                }

                // Default row rendering
                return (
                  <TableRow
                    key={rowKeyGetter(item, index)}
                    onClick={
                      onRowClick ? () => onRowClick(item, index) : undefined
                    }
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                    className={tableRowClassName}
                    onMouseEnter={
                      onHoverRow ? () => onHoverRow(item, index) : undefined
                    }
                  >
                    {columns.map(column => (
                      <TableCell
                        key={column.key}
                        align={column.align || 'left'}
                        className={column.className}
                      >
                        {column.render
                          ? column.render(item, index)
                          : item[column.key] !== undefined
                          ? item[column.key]
                          : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <NoHotelsText>{emptyMessage}</NoHotelsText>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    );
  };

  // Conditionally wrap with TableWrapper
  if (showWrapper) {
    return (
      <TableWrapper className={className}>{renderTableContent()}</TableWrapper>
    );
  }

  return <div className={className}>{renderTableContent()}</div>;
};
export default DataTable;
